/**
 * Created by
 * Description:
 *
 * ------ maintenance history ------
 * Modified by Bowen Li on 2019/8/8: value in finder cannot be selected after tap
 */
import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, Input, ChangeDetectorRef } from '@angular/core';
import { Subject, BehaviorSubject, Subscription } from 'rxjs';
import { take, map, debounceTime, filter } from 'rxjs/operators';
import { FinderConfig, FinderSetting, ResearchFocusStatus, FinderActions } from './finder.view-model';
import { EntityService } from '../../tamalelibs/services/entity.service';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'finder',
    templateUrl: './finder.component.html',
    styleUrls: ['./finder.component.scss']
})
export class FinderComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() config: FinderConfig;
    @ViewChild('finder', { static: true }) finderEle: any;

    currentState: FinderSetting = new FinderSetting();
    finderValue: any;
    kDataSource: BehaviorSubject<any> = new BehaviorSubject<any>([]);
    isFinderLoading = false;
    isIPAD = false;

    private _destroySubscriptions: Array<Subscription> = [];
    private _filterHandler$: Subject<string> = new Subject();
    private _serverFilterSubscription: Subscription;
    private _wrapperElement: any;

    constructor(
        private _deviceService: DeviceDetectorService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _entityService: EntityService,
    ) {
        this.isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
    }

    ngOnInit() {
        this._destroySubscriptions.push(
            this._filterHandler$.pipe(
                debounceTime(250),
                filter(filterStr => filterStr != null && filterStr.length > 0)
            ).subscribe(filterStr => this._filterHandler(filterStr)),
            this.config.actions$.subscribe(action => this._actionHandler(action))
        );

        // handle re-initialize during layout change
        if (this.config.initState) {
            this._actionHandler({
                type: 'SET_VALUE',
                payload: this.config.initState
            });
        }
    }

    ngAfterViewInit() {
        // hide the popup button to let combobox looks like autocomplete

        if (this.finderEle && this.finderEle.wrapper) {
            this._wrapperElement = 'querySelector' in this.finderEle.wrapper ? this.finderEle.wrapper : this.finderEle.wrapper.nativeElement;
            if (this._wrapperElement) {
                this._wrapperElement.querySelector('.k-select').style.display = 'none';
                this._wrapperElement.querySelector('.k-input').setAttribute('tid', 'entity_finder');
                this._wrapperElement.querySelector('.k-input').setAttribute('autocorrect', 'off');
                this._wrapperElement.querySelector('.k-input').setAttribute('spellcheck', 'false');
                this._wrapperElement.querySelector('.k-input').setAttribute('autocapitalize', 'off');
                this._wrapperElement.querySelector('.k-input').addEventListener('keyup', e => this._onFinderKeyup(e), false);
                this._wrapperElement.querySelector('.k-input').addEventListener('blur', e => this._onFinderBlur(e), false);
            }
        }
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription && subscription.unsubscribe());
    }

    onFinderFilterChange(filterStr) {
        this.finderEle.toggle(false);
        this._filterHandler$.next(filterStr);
    }

    onFinderFocus() {
        if (!this.isIPAD) {
            if (this._wrapperElement) {
                this._wrapperElement.querySelector('.k-input').select();
            }
        } else {
            setTimeout(() => {
                if (this._wrapperElement) {
                    this._wrapperElement.querySelector('.k-input').setSelectionRange(0, 9999);
                }
            }, 0);
        }
    }

    onValueChange(value) {
        if (!value) {
            this.kDataSource.next([this.currentState.entity]);
            this.finderEle.value = this.currentState.entity;
            return;
        }

        if (value.id !== this.currentState.entity.id) {
            this.currentState.entity = value;
            this.currentState.status = ResearchFocusStatus.NONE;
            this.config.events$.next({
                type: 'CHANGE',
                payload: this.currentState
            });
        }
    }

    onStatusChange(status) {
        this.currentState.status = (this.currentState.status === status) ? ResearchFocusStatus.NONE : status;
        this.config.events$.next({
            type: 'CHANGE',
            payload: this.currentState
        });
    }

    private _actionHandler(action) {
        if (action.type === 'SET_VALUE') {
            this.currentState = action.payload;
            this.kDataSource.next([action.payload.entity]);
            this.finderEle.value = action.payload.entity;
            this._changeDetectorRef.detectChanges();
        } else if (action.type === FinderActions.SET_VALUE_STATUS) {
            this.currentState.status = action.payload;
        } else if (action.type === FinderActions.FOCUS_FINDER) {
            this.finderEle.focus();
        }
    }

    private _filterHandler(filterStr: string) {
        this._toggleKendoComboBoxLoading(true);
        this._serverFilterSubscription = this._entityService.getEntityListBySearchTextQuick(1, 10, filterStr).pipe(
            take(1),
            map(response => this._entityService.mapEntityBriefList(response))
        ).subscribe(data => {
            this.kDataSource.next(data);
            this._toggleKendoComboBoxLoading(false);
            this.finderEle.toggle(true);
        });
    }

    private _onFinderBlur(event) {
        if (this.isFinderLoading) {
            // handle the case: user blur the finder when loading data will cancel the filter request
            this._toggleKendoComboBoxLoading(false);
            this._serverFilterSubscription.unsubscribe();
        }
    }

    private _onFinderKeyup(event) {
        if (this.isFinderLoading) {
            // handle the case: press ENTER when loading data will cancel the filter request
            this._toggleKendoComboBoxLoading(false);
            this._serverFilterSubscription.unsubscribe();
        }
    }

    private _toggleKendoComboBoxLoading(loading: boolean) {
        if (loading) {
            this.isFinderLoading = true;
            if (this._wrapperElement) {
                this._wrapperElement.querySelector('.k-select').style.display = 'flex';
            }
        } else {
            this.isFinderLoading = false;
            if (this._wrapperElement) {
                this._wrapperElement.querySelector('.k-select').style.display = 'none';
            }
        }
    }
}
